import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useUserPreferences } from './UserPreferencesContext'

export interface SearchContextType {
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  clearSearch: () => void
}

const SearchContext = createContext<SearchContextType>({
  search: '',
  setSearch: () => {},
  clearSearch: () => {},
})

export const useSearch = () => useContext(SearchContext)

const normalizePathForSearch = (pathname: string): string => {
  const cleanPath = pathname.trim().replace(/\/+$/, '').toLowerCase() || 'root'
  const normalized = cleanPath
    .replace(/\/edit(\/[^/]+)?$/, '')
    .replace(/\/register(\/[^/]+)?$/, '')
    .replace(/\/create(\/[^/]+)?$/, '')
    .replace(/\/view(\/[^/]+)?$/, '')
    .replace(/\/new(\/[^/]+)?$/, '')
    .replace(/\/ticket(\/[^/]+)?$/, '')
  return normalized || 'root'
}

const getSearchKeyForPath = (pathname: string): string => {
  const normalized = normalizePathForSearch(pathname)
  const sanitized = normalized.replace(/^\//, '').replace(/[^a-z0-9_-]/g, '_')
  return `search_${sanitized}`
}

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getPreference, setPreference } = useUserPreferences()
  const location = useLocation()
  const currentScopeKey = getSearchKeyForPath(location.pathname)

  const [localDrafts, setLocalDrafts] = useState<Record<string, string>>({})
  const currentScopeKeyRef = useRef(currentScopeKey)
  currentScopeKeyRef.current = currentScopeKey
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const pendingSaveRef = useRef<{ key: string; value: string } | null>(null)

  useEffect(() => {
    if (pendingSaveRef.current && pendingSaveRef.current.key !== currentScopeKey) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      const { key, value } = pendingSaveRef.current
      setPreference(key, value.trim() ? value : null)
      pendingSaveRef.current = null
    }

    return () => {
      if (pendingSaveRef.current) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          debounceTimerRef.current = null
        }
        const { key, value } = pendingSaveRef.current
        setPreference(key, value.trim() ? value : null)
        pendingSaveRef.current = null
      }
    }
  }, [currentScopeKey, setPreference])

  const search = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(localDrafts, currentScopeKey)) {
      return localDrafts[currentScopeKey]
    }
    const saved = getPreference<string | null | undefined>(currentScopeKey, '')
    return typeof saved === 'string' ? saved : ''
  }, [currentScopeKey, localDrafts, getPreference])

  const setSearch: React.Dispatch<React.SetStateAction<string>> = useCallback((valueOrUpdater) => {
    const targetKey = currentScopeKeyRef.current

    setLocalDrafts((prev) => {
      const prevValue = Object.prototype.hasOwnProperty.call(prev, targetKey)
        ? prev[targetKey]
        : (getPreference<string | null | undefined>(targetKey, '') || '')
      const nextValue = typeof valueOrUpdater === 'function' ? (valueOrUpdater as any)(prevValue) : valueOrUpdater
      const safeValue = typeof nextValue === 'string' ? nextValue : ''

      pendingSaveRef.current = { key: targetKey, value: safeValue }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        setPreference(targetKey, safeValue.trim() ? safeValue : null)
        pendingSaveRef.current = null
        debounceTimerRef.current = null
      }, 400)

      return {
        ...prev,
        [targetKey]: safeValue,
      }
    })
  }, [getPreference, setPreference])

  const clearSearch = useCallback(() => {
    const targetKey = currentScopeKeyRef.current
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    pendingSaveRef.current = null
    setLocalDrafts((prev) => ({
      ...prev,
      [targetKey]: '',
    }))
    setPreference(targetKey, null)
  }, [setPreference])

  return (
    <SearchContext.Provider value={{ search, setSearch, clearSearch }}>
      {children}
    </SearchContext.Provider>
  )
}

export default SearchContext
